import React, { useState, useEffect } from 'react';
import { useSettings } from '../../../context/settingsContext';
import { Form, Input, Card, Spin, Button, message, Space, Modal } from 'antd';
import axios from '../../../context/axiosConfig';

const Comments = () => {
  const { settings, loading } = useSettings();
  const [commentData, setCommentData] = useState([]);
  const [commentNames, setCommentNames] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newFragment, setNewFragment] = useState({ text: '', name: '' });
  const [selectedName, setSelectedName] = useState('');
  const [editableFragments, setEditableFragments] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [fragmentToDelete, setFragmentToDelete] = useState(null);

  const fetchComments = async () => {
    try {
      const response = await axios.post('/settings/comments/fragments');
      setCommentData(response.data.fragments);
      setCommentNames(response.data.names);
    } catch (error) {
      message.error('Wystąpił błąd podczas pobierania fragmentów komentarzy');
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleAddFragment = async () => {
    try {
      await axios.post('/settings/comments/fragment/add', {
        comment_name: selectedName,
        text: newFragment.text,
        name: newFragment.name
      });
      await fetchComments();
      setIsModalVisible(false);
      setNewFragment({ text: '', name: '' });
      message.success('Fragment został dodany');
    } catch (error) {
      message.error('Wystąpił błąd podczas dodawania fragmentu');
    }
  };

  const showDeleteConfirm = (fragmentId) => {
    setFragmentToDelete(fragmentId);
    setDeleteModalVisible(true);
  };

  const handleRemoveFragment = async () => {
    try {
      await axios.post('/settings/comments/fragment/delete', { fragmentId: fragmentToDelete });
      await fetchComments();
      setDeleteModalVisible(false);
      setFragmentToDelete(null);
      message.success('Fragment został usunięty');
    } catch (error) {
      message.error('Wystąpił błąd podczas usuwania fragmentu');
    }
  };

  const handleEditFragment = async (fragmentId) => {
    const fragment = commentData.find(f => f.msr_comments_fragments_id === fragmentId);
    try {
      await axios.post('/settings/comments/fragment/update', {
        fragmentId,
        text: fragment.msr_comments_fragments_text,
        name: fragment.msr_comments_fragments_name
      });
      await fetchComments();
      setEditableFragments({ ...editableFragments, [fragmentId]: false });
      message.success('Fragment został zaktualizowany');
    } catch (error) {
      message.error('Wystąpił błąd podczas edycji fragmentu');
    }
  };

  const toggleEdit = (fragmentId) => {
    setEditableFragments({ ...editableFragments, [fragmentId]: !editableFragments[fragmentId] });
  };

  const handleCancelEdit = async (fragmentId) => {
    await fetchComments();
    setEditableFragments({ ...editableFragments, [fragmentId]: false });
  };

  const updateFragmentField = (fragmentId, field, value) => {
    setCommentData(commentData.map(fragment =>
      fragment.msr_comments_fragments_id === fragmentId
        ? { ...fragment, [field]: value }
        : fragment
    ));
  };

  if (loading) {
    return <Spin />;
  }

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }}>
        {commentNames.map(name => (
          <Card key={name.msr_comments_names_to_fragments_key} title={name.msr_comments_names_to_fragments_title} style={{ marginTop: 16 }}>
            <Button 
              type="primary" 
              onClick={() => {
                setSelectedName(name.msr_comments_names_to_fragments_key);
                setIsModalVisible(true);
              }}
              style={{ marginBottom: 16 }}
            >
              Dodaj nowy fragment
            </Button>
            {commentData
              .filter(fragment => fragment.msr_comments_fragments_comment_name === name.msr_comments_names_to_fragments_key)
              .map(fragment => (
                <Card key={fragment.msr_comments_fragments_id} style={{ marginTop: 16, padding: '0' }} className='card-with-buttons'>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item label="Nazwa fragmentu" className='horizontal-item'>
                      {editableFragments[fragment.msr_comments_fragments_id] ? (
                        <Input
                          value={fragment.msr_comments_fragments_name}
                          onChange={(e) => updateFragmentField(fragment.msr_comments_fragments_id, 'msr_comments_fragments_name', e.target.value)}
                          placeholder="Nazwa fragmentu"
                        />
                      ) : (
                        <div style={{fontSize: '16px', fontWeight: '600'}}>{fragment.msr_comments_fragments_name}</div>
                      )}
                    </Form.Item>
                    <Form.Item label="Tekst fragmentu" className='horizontal-item'>
                      {editableFragments[fragment.msr_comments_fragments_id] ? (
                        <Input.TextArea
                          value={fragment.msr_comments_fragments_text}
                          onChange={(e) => updateFragmentField(fragment.msr_comments_fragments_id, 'msr_comments_fragments_text', e.target.value)}
                          rows={4}
                        />
                      ) : (
                        <div style={{fontSize: '16px', fontWeight: '600'}}>{fragment.msr_comments_fragments_text}</div>
                      )}
                    </Form.Item>
                    <Space>
                      {editableFragments[fragment.msr_comments_fragments_id] ? (
                        <>
                          <Button type="primary" onClick={() => handleEditFragment(fragment.msr_comments_fragments_id)}>
                            Zapisz
                          </Button>
                          <Button onClick={() => handleCancelEdit(fragment.msr_comments_fragments_id)}>
                            Anuluj
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => toggleEdit(fragment.msr_comments_fragments_id)}>
                          Edytuj
                        </Button>
                      )}
                      <Button type="primary" danger onClick={() => showDeleteConfirm(fragment.msr_comments_fragments_id)}>
                        Usuń fragment
                      </Button>
                    </Space>
                  </Space>
                </Card>
              ))}
          </Card>
        ))}
      </Space>

      <Modal
        title="Dodaj nowy fragment"
        visible={isModalVisible}
        onOk={handleAddFragment}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Nazwa fragmentu">
            <Input
              value={newFragment.name}
              onChange={(e) => setNewFragment({ ...newFragment, name: e.target.value })}
            />
          </Form.Item>
          <Form.Item label="Tekst fragmentu">
            <Input.TextArea
              value={newFragment.text}
              onChange={(e) => setNewFragment({ ...newFragment, text: e.target.value })}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Potwierdź usunięcie"
        visible={deleteModalVisible}
        onOk={handleRemoveFragment}
        onCancel={() => {
          setDeleteModalVisible(false);
          setFragmentToDelete(null);
        }}
      >
        <p>Czy na pewno chcesz usunąć ten fragment?</p>
      </Modal>
    </Card>
  );
};

export default Comments;